import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSupervisor } from '@/hooks/useSupervisor';
import { DynamicComponent } from '../dynamic/DynamicComponent';
import { useAppSelector } from '@/app/hooks';
import { selectOrderState } from '@/features/order/orderSlice';
import { buildCanonicalOrder } from '@/features/order/utils/canonicalOrder';
import EtsyOrdersCanvas from '@/components/etsy/EtsyOrdersCanvas';
import EtsyImportCanvas from '@/components/etsy/EtsyImportCanvas';
import EtsyMatchingCanvas from '@/components/etsy/EtsyMatchingCanvas';
import EtsySetupCanvas from '@/components/etsy/EtsySetupCanvas';
import EtsyToProductionCanvas from '@/components/etsy/EtsyToProductionCanvas';
import { getWorkspaceCanvasMode, WORKSPACE_CANVAS_MODES, WORKSPACE_CANVAS_QUERY_KEY } from '@/components/workspace/workspaceMode';

export const CanvasPanel = () => {
  const [searchParams] = useSearchParams();
  const { state: supervisorState } = useSupervisor();
  const reduxOrder = useAppSelector(selectOrderState);
  const workspaceCanvasMode = getWorkspaceCanvasMode(searchParams.get(WORKSPACE_CANVAS_QUERY_KEY));

  // Derive a stable state key like 'screen.upload' or 'screen.login' from XState value
  const derivedName = useMemo(() => {
    if (workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsySetup) {
      return 'integration.etsy.setup';
    }
    if (workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyOrders) {
      return 'integration.etsy.orders';
    }
    if (workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyImport) {
      return 'integration.etsy.import';
    }
    if (workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyMatching) {
      return 'integration.etsy.matching';
    }
    if (workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyToProduction) {
      return 'integration.etsy.to-production';
    }

    const deriveStateName = (value: any): string => {
      if (typeof value === 'string') return value;
      if (value && typeof value === 'object') {
        const [top, sub] = Object.entries(value)[0] as [string, any];
        if (typeof sub === 'string') return `${top}.${sub}`;
        return top;
      }
      return 'screen.upload';
    };

    return deriveStateName(supervisorState.value as any);
  }, [supervisorState.value, workspaceCanvasMode]);

  const canonicalOrder = useMemo(() => {
    return buildCanonicalOrder(reduxOrder, supervisorState.context.order);
  }, [reduxOrder, supervisorState.context.order]);

  const renderContent = () => {
    if (workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsySetup) {
      return <EtsySetupCanvas />;
    }

    if (workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyOrders) {
      return <EtsyOrdersCanvas />;
    }

    if (workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyImport) {
      return <EtsyImportCanvas />;
    }

    if (workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyMatching) {
      return <EtsyMatchingCanvas />;
    }

    if (workspaceCanvasMode === WORKSPACE_CANVAS_MODES.etsyToProduction) {
      return <EtsyToProductionCanvas />;
    }

    // The 'isLoading' state can be derived from the supervisor state if needed in the future.
    // For now, we'll just render based on the current state.

    const uiPayload = {
      name: derivedName, // e.g., 'screen.upload', 'screen.login', 'screen.paper'
      props: { order: canonicalOrder },
    };

    return <DynamicComponent uiPayload={uiPayload} />;
  };

  return (
    <div className="flex bg-muted/40">
      <div className="relative flex w-full items-start justify-center px-4 pb-10 pt-0">
        {renderContent()}
      </div>
    </div>
  );
};
